import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Upload, FolderSync, Loader2, FileVideo, CheckCircle2, Timer, Calendar as CalendarIcon, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function UploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [channel, setChannel] = useState("");
  const [recordingDate, setRecordingDate] = useState(null); // Date | null
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0..100
  const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 });
  const [uploadStarted, setUploadStarted] = useState(null); // timestamp ms
  const [scanning, setScanning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [watchInfo, setWatchInfo] = useState(null);
  const [maxMb, setMaxMb] = useState(500);
  const maxBytes = maxMb * 1024 * 1024;

  useEffect(() => {
    api.get("/upload-config").then((r) => setMaxMb(r.data.max_mb)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user.role === "admin") {
      api.get("/folder-watch/status").then((r) => setWatchInfo(r.data)).catch(() => {});
    }
  }, [user.role]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const acceptFile = (f) => {
    if (f.size > maxBytes) {
      toast.error(`File terlalu besar (${(f.size / (1024 * 1024)).toFixed(1)} MB). Maksimum ${maxMb} MB.`);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pilih file video terlebih dahulu");
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`File terlalu besar (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maksimum ${maxMb} MB.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("channel_name", channel);
    fd.append("recording_date", recordingDate ? format(recordingDate, "yyyy-MM-dd") : "");
    setUploading(true);
    setUploadProgress(0);
    setUploadBytes({ loaded: 0, total: file.size });
    setUploadStarted(Date.now());
    try {
      const { data } = await api.post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0, // unlimited — file size already capped at maxBytes
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (evt) => {
          const total = evt.total || file.size;
          const loaded = evt.loaded || 0;
          setUploadBytes({ loaded, total });
          setUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : 0);
        },
      });
      toast.success(`File "${data.file_name}" diantrekan. Transkripsi dimulai.`);
      setFile(null);
      setChannel("");
      setRecordingDate(null);
      if (fileRef.current) fileRef.current.value = "";
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStarted(null);
    }
  };

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const uploadETA = (() => {
    if (!uploadStarted || uploadBytes.loaded === 0 || uploadProgress >= 100) return null;
    const elapsed = (Date.now() - uploadStarted) / 1000;
    const rate = uploadBytes.loaded / elapsed; // bytes/s
    if (rate <= 0) return null;
    const remaining = (uploadBytes.total - uploadBytes.loaded) / rate;
    return { rate, remaining };
  })();

  const scanFolder = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await api.post("/videos/scan-folder");
      setScanResult(data);
      toast.success(`Scan selesai · ${data.queued} file diantrekan`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="px-6 py-6 max-w-3xl mx-auto space-y-6" data-testid="upload-page">
      <header>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ingest</p>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tambah Rekaman DVR</h1>
        <p className="text-sm text-slate-500 mt-1">
          Unggah file AVI manual atau pindai folder DVR yang ter-mount di server.
        </p>
      </header>

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-sm p-6 space-y-5"
        data-testid="upload-form"
      >
        <p className="text-sm font-semibold flex items-center gap-2">
          <Upload className="h-4 w-4 text-slate-500" /> Upload Manual
        </p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
            dragOver ? "border-slate-900 bg-slate-100" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
          }`}
          data-testid="upload-dropzone"
        >
          <FileVideo className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="text-sm mt-2 font-medium text-slate-800">
            {file ? file.name : "Drop file AVI / MP4 / MKV di sini, atau klik untuk pilih"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {file ? `${formatBytes(file.size)}` : `Maksimum ${maxMb} MB per file · tanpa batas waktu upload`}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".avi,.mp4,.mkv,.mov,.mpeg,.mpg,.ts,.webm"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) acceptFile(f);
            }}
            data-testid="upload-file-input"
          />
        </div>

        <div className="flex items-start gap-2 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-800">Batas ukuran file: {maxMb} MB</p>
            <p className="leading-relaxed mt-0.5">
              File lebih besar dari {maxMb} MB akan ditolak otomatis (validasi client &amp; server).
              Tidak ada batas durasi upload — proses berjalan sampai selesai sesuai bandwidth Anda.
              Untuk file sangat besar, gunakan <b>Folder Watch</b>.
            </p>
          </div>
        </div>

        {uploading && (
          <div className="rounded-sm border border-slate-300 bg-white p-4 space-y-2" data-testid="upload-progress-panel">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-slate-700 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                Mengunggah · {uploadProgress}%
              </span>
              <span className="font-mono text-slate-600">
                {formatBytes(uploadBytes.loaded)} / {formatBytes(uploadBytes.total)}
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-sm overflow-hidden">
              <div
                className={`h-full transition-all duration-200 ${uploadProgress >= 100 ? "bg-emerald-500" : "bg-slate-900"}`}
                style={{ width: `${uploadProgress}%` }}
                data-testid="upload-progress-bar"
              />
            </div>
            {uploadETA && (
              <p className="text-[11px] text-slate-500 font-mono">
                {(uploadETA.rate / (1024 * 1024)).toFixed(1)} MB/s · sisa ~{Math.ceil(uploadETA.remaining)} detik
              </p>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="channel" className="text-xs uppercase tracking-wider text-slate-600">Channel</Label>
            <Input
              id="channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="Misal: TVRI, Metro TV, Kompas TV"
              className="mt-1 rounded-sm border-slate-300"
              data-testid="upload-channel-input"
            />
          </div>
          <div>
            <Label htmlFor="recdate" className="text-xs uppercase tracking-wider text-slate-600">Tanggal Rekaman</Label>
            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  id="recdate"
                  type="button"
                  className="mt-1 w-full flex items-center justify-between rounded-sm border border-slate-300 bg-white text-sm px-3 py-2 hover:border-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  data-testid="upload-date-trigger"
                >
                  <span className={recordingDate ? "text-slate-900" : "text-slate-400"}>
                    {recordingDate ? format(recordingDate, "d MMMM yyyy", { locale: idLocale }) : "Pilih tanggal rekaman"}
                  </span>
                  <CalendarIcon className="h-4 w-4 text-slate-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 rounded-sm border-slate-200"
                align="start"
                data-testid="upload-date-popover"
              >
                <Calendar
                  mode="single"
                  selected={recordingDate}
                  onSelect={(d) => {
                    setRecordingDate(d);
                    setDatePopoverOpen(false);
                  }}
                  locale={idLocale}
                  disabled={(d) => d > new Date()}
                  initialFocus
                />
                {recordingDate && (
                  <div className="border-t border-slate-200 px-3 py-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => { setRecordingDate(null); setDatePopoverOpen(false); }}
                      className="text-xs text-slate-500 hover:text-slate-900 underline"
                      data-testid="upload-date-clear"
                    >
                      Kosongkan
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <Button
          type="submit"
          disabled={uploading || !file}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
          data-testid="upload-submit-button"
        >
          {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {uploading ? `Mengunggah... ${uploadProgress}%` : "Ingest & Transkrip"}
        </Button>
      </form>

      {user.role === "admin" && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4" data-testid="folder-watch-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold flex items-center gap-2">
              <FolderSync className="h-4 w-4 text-slate-500" /> Folder Watch
            </p>
            {watchInfo?.enabled && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-800" data-testid="auto-scan-indicator">
                <Timer className="h-3 w-3" />
                Auto-scan setiap {watchInfo.interval_seconds}s
              </span>
            )}
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Folder ingest: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded-sm">{watchInfo?.ingest_dir || "/app/data/ingest"}</span>
            <br />
            Sistem otomatis memindai folder ini setiap <b>{watchInfo?.interval_seconds || 60} detik</b> dan
            mengantrekan file baru ke pipeline transkripsi. Anda juga bisa memicu scan manual kapan saja.
          </p>
          <Button
            onClick={scanFolder}
            disabled={scanning}
            variant="outline"
            className="rounded-sm border-slate-300"
            data-testid="scan-folder-button"
          >
            {scanning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <FolderSync className="h-4 w-4 mr-2" />
            Scan Manual Sekarang
          </Button>
          {scanResult && (
            <div className="flex items-center gap-2 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="scan-result">
              <CheckCircle2 className="h-4 w-4" />
              Discovered <b>{scanResult.scanned}</b>, queued <b>{scanResult.queued}</b>, skipped duplikat <b>{scanResult.skipped}</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
